/**
 * Call History → "Upload calls".
 *
 * Visible only when the tenant's `manual_call_history_upload` flag is on
 * (GET /reports/calls/manual-upload/config — hidden, not merely disabled,
 * because an agent on a tenant without the feature should never see it).
 *
 * Three steps:
 *   1. Type — "Call recording" (audio → encrypt → Transcribe → QM),
 *      "Chat transcript" (text file → stored as a completed transcript →
 *      the QM auditor scores it directly) or "Manual CDR entry" (a typed
 *      call-detail record — no file at all).
 *   2. Files & details — one or more files of the chosen kind, each with its
 *      editable CDR fields (customer number, agent dropdown, direction,
 *      time). For recordings an optional CDR file (csv/json) fills the same
 *      fields in bulk. For kind='cdr' this step IS the typed form.
 *   3. Review — recordings get an <audio> player (with a server-transcode
 *      fallback for telephone codecs); transcripts get a text preview.
 *      Only on "Upload" is each file POSTed to /reports/calls/manual-upload.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import Select from '../../ui/Select.js'
import {
  AlertTriangle, CheckCircle2, FileAudio, FileText, FileUp,
  Loader2, MessageSquare, Phone, Trash2, Upload, XCircle,
} from 'lucide-react'

import { api } from '../../../api/client.js'
import { useAuthStore } from '../../../store/authStore.js'
import Drawer from '../../ui/Drawer.js'
import { useToast } from '../../system/ToastProvider.js'

const ACCEPT_AUDIO = 'audio/*,.wav,.mp3,.m4a,.mp4,.flac,.ogg,.amr,.webm'
const ACCEPT_TEXT = '.txt,.json,.csv,.md,.log,.pdf,text/*,application/pdf'
const R = 'rounded-[3px]'

type UploadKind = 'recording' | 'chat'

interface AgentOption {
  id: string
  name: string | null
  extension: string | null
}

interface QmFormOption {
  id: string
  name: string
  isDefault: boolean
}

interface UploadItem {
  file: File
  url: string
  durationSec: number | null
  customerNumber: string
  /** Agent picked from the tenant agent dropdown (agents.id). */
  agentId: string
  agentNumber: string
  direction: 'inbound' | 'outbound'
  startTime: string
  status: 'idle' | 'uploading' | 'done' | 'error'
  error?: string | undefined
  /** Chat kind: first chars of the transcript for the review step. */
  textPreview?: string
  /** Non-PCM codecs (μ-law/GSM WAV…) need a server-transcoded preview. */
  previewBusy?: boolean
  previewTried?: boolean
  previewConverted?: boolean
  previewFailed?: boolean
  /** SHA-256 of the file content — matches the hash embedded in storage_key. */
  hash?: string
  /** Server check: the exact same file was uploaded before. */
  duplicate?: boolean
  /** Server check: same size+duration exists (heuristic, older rows). */
  possibleDup?: boolean
  /** Extra call-history fields supplied by a parsed FreeSWITCH CDR row. */
  queueName?: string | undefined
  hangupCause?: string | undefined
  linkedId?: string | undefined
  answerTime?: string | undefined
  billSec?: number | undefined
  queueWaitSec?: number | undefined
  hangupParty?: 'caller' | 'callee' | 'system' | undefined
  did?: string | undefined
  destinationType?: string | undefined
  destination?: string | undefined
}

function nowLocal(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

function fmtBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${Math.round(n / 1024)} KB`
  return `${n} B`
}

function fmtSec(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—'
  const s = Math.round(n)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Client-side guard, below the API's 200 MB cap. */
const MAX_FILE_BYTES = 190 * 1024 * 1024

/**
 * Phone-shaped digit runs in a filename ("call_+18583127722.wav") make a good
 * customer-number guess — external exports usually carry the CLI in the name.
 */
function numberFromFilename(name: string): string {
  const m = /(\+?[\d][\d .()/-]{5,}\d)/.exec(name)
  if (!m) return ''
  const digits = (m[1] ?? '').replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15 ? (m[1] ?? '').trim() : ''
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/* ------------------------------------------------------------------ */
/* CDR file parsing — csv or json rows keyed on the audio file name.     */
/* ------------------------------------------------------------------ */

const CDR_KEYS: Record<string, string[]> = {
  fileName: ['filename', 'file', 'name', 'recording', 'recordingfile', 'audio', 'audiofile'],
  customerNumber: ['customernumber', 'customer', 'caller', 'from', 'fromnumber', 'src', 'source'],
  agentNumber: ['agentnumber', 'agent', 'agentext', 'agentextension', 'extension', 'to', 'tonumber', 'dst', 'destination'],
  direction: ['direction', 'dir', 'calltype'],
  startTime: ['starttime', 'start', 'calldate', 'datetime', 'date', 'time', 'timestamp', 'calltime'],
  durationSec: ['duration', 'durationsec', 'length', 'seconds', 'calllength'],
}

interface CdrRow {
  fileName?: string | undefined
  customerNumber?: string | undefined
  agentNumber?: string | undefined
  direction?: 'inbound' | 'outbound' | undefined
  startTime?: string | undefined
  durationSec?: number | undefined
  /** Extra call-history fields — populated from a FreeSWITCH CDR_REPORT. */
  queueName?: string | undefined
  hangupCause?: string | undefined
  linkedId?: string | undefined
  answerTime?: string | undefined
  billSec?: number | undefined
  queueWaitSec?: number | undefined
  hangupParty?: 'caller' | 'callee' | 'system' | undefined
  /** The number the customer dialled (DID) — calls.to_number on inbound. */
  did?: string | undefined
  destinationType?: string | undefined
  destination?: string | undefined
}

function stripDomain(s: string): string {
  return s.split('@')[0] ?? s
}

/**
 * Parse a FreeSWITCH CDR (the CDR_REPORT / variables shape exported by the
 * old Krupto platform) into our flat CdrRow. Returns null when the object
 * isn't that shape.
 */
function rowFromFreeSwitch(rec: Record<string, unknown>): CdrRow | null {
  const rep = (rec['CDR_REPORT'] ?? rec['cdr_report'] ?? rec['cdr']) as Record<string, unknown> | undefined
  const vars = (rep?.['variables'] ?? rep?.['channel_variables'] ?? rec['variables']) as Record<string, unknown> | undefined
  if (!vars || typeof vars !== 'object' || Object.keys(vars).length === 0) return null
  const str = (k: string): string | undefined => {
    const x = vars[k]
    return typeof x === 'string' && x.trim() !== '' ? x.trim() : undefined
  }
  const num = (k: string): number | undefined => {
    const n = Number(vars[k])
    return Number.isFinite(n) && n > 0 ? n : undefined
  }

  const dir = (str('direction') ?? str('krupto_direction') ?? '').toLowerCase()
  const inbound = dir.startsWith('in')
  const out: CdrRow = {}

  const recordFile = str('cc_record_filename')
  if (recordFile) out.fileName = baseName(recordFile)
  else if (str('uuid') !== undefined || str('call_uuid') !== undefined) {
    // No filename column — the uuid usually appears inside the recording's
    // file name, which is good enough for our basename matching.
    out.fileName = str('uuid') ?? str('call_uuid')
  }

  const customer = inbound
    ? (str('sip_from_user') ?? str('original_caller_id_number') ?? str('caller_id_number'))
    : (str('original_destination_number') ?? str('sip_to_user') ?? str('destination_number'))
  if (customer !== undefined) out.customerNumber = customer
  const agent = str('cc_agent') ?? str('last_sent_callee_id_number')
  if (agent !== undefined) out.agentNumber = stripDomain(agent)
  if (dir !== '') out.direction = inbound ? 'inbound' : 'outbound'

  const start = str('start_stamp') ?? (num('start_epoch') !== undefined ? new Date(num('start_epoch')! * 1000).toISOString() : undefined)
  if (start !== undefined) out.startTime = start
  out.durationSec = num('billsec') ?? num('duration') ?? num('record_seconds')
  out.billSec = num('billsec')
  out.answerTime = str('answer_stamp')
  out.queueName = str('cc_queue') !== undefined ? stripDomain(str('cc_queue')!) : undefined
  out.hangupCause = str('hangup_cause') ?? str('bridge_hangup_cause')
  out.linkedId = str('uuid') ?? str('call_uuid') ?? str('krupto_uuid')
  // DID + destination — what fills the report's did / destinationType columns.
  out.did = str('krupto_did') ?? str('sip_req_user') ?? str('original_destination_number')
  const dtype = str('krupto_destination_type')
  out.destinationType =
    dtype === 'callcenter' ? 'QUEUE' :
    dtype === 'ivr' ? 'ivr' :
    dtype === 'playngo' ? 'Play N Go' : dtype
  out.destination = out.queueName
    ?? (str('current_application_data') !== undefined ? stripDomain(str('current_application_data')!) : undefined)
  const joined = num('cc_queue_joined_epoch'); const answered = num('cc_queue_answered_epoch')
  if (joined !== undefined && answered !== undefined && answered >= joined) out.queueWaitSec = answered - joined
  const disp = str('sip_hangup_disposition')
  if (disp === 'recv_bye') out.hangupParty = 'caller'
  else if (disp === 'send_bye') out.hangupParty = 'callee'
  return out
}

function normKey(k: string): string {
  return k.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function rowFromRecord(rec: Record<string, unknown>): CdrRow {
  const out: CdrRow = {}
  const mapped = new Map<string, string>()
  for (const [k, v] of Object.entries(rec)) {
    const nk = normKey(k)
    for (const [field, aliases] of Object.entries(CDR_KEYS)) {
      if (aliases.includes(nk)) mapped.set(field, String(v).trim())
    }
  }
  if (mapped.has('fileName')) out.fileName = mapped.get('fileName')
  if (mapped.has('customerNumber')) out.customerNumber = mapped.get('customerNumber')
  if (mapped.has('agentNumber')) out.agentNumber = mapped.get('agentNumber')
  const dir = (mapped.get('direction') ?? '').toLowerCase()
  if (dir.startsWith('in')) out.direction = 'inbound'
  else if (dir.startsWith('out')) out.direction = 'outbound'
  if (mapped.has('startTime')) out.startTime = mapped.get('startTime')
  const dur = Number(mapped.get('durationSec'))
  if (Number.isFinite(dur) && dur > 0) out.durationSec = Math.round(dur)
  return out
}

function parseCsv(text: string): Record<string, unknown>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '')
  if (lines.length < 2) return []
  const split = (l: string): string[] => {
    const out: string[] = []
    let cur = '', q = false
    for (const ch of l) {
      if (ch === '"') { q = !q; continue }
      if (ch === ',' && !q) { out.push(cur); cur = ''; continue }
      cur += ch
    }
    out.push(cur)
    return out.map((s) => s.trim())
  }
  const headers = split(lines[0] ?? '')
  return lines.slice(1).map((line) =>
    Object.fromEntries(headers.map((h, i) => [h, split(line)[i] ?? ''])),
  )
}

/**
 * Clean text copied out of a JSON *viewer* (like the Krupto CDR inspector):
 * it annotates containers with "N items", renders array elements as numeric
 * pseudo-keys (`0:{`), collapses hidden content to `...` lines, and often
 * drops the outer braces. Returns text JSON.parse can handle.
 */
function sanitizeViewerJson(raw: string): string {
  let t = raw.trim()
  // `:{ 236 items` / `:[5 items` / `] 73 items` — count annotations.
  t = t.replace(/([{[])\s*\d+\s*items?/g, '$1')
  t = t.replace(/([}\]])\s*\d+\s*items?/g, '$1')
  // `0:{` / `1:{` — viewer index keys for array elements.
  t = t.replace(/(^|[,\s[])\d+\s*:\s*(?=[{[])/gm, '$1')
  // `...` lines — collapsed content the viewer never showed.
  t = t.replace(/^[ \t]*\.\.\.[ \t]*,?[ \t]*$/gm, '')
  // The viewer renders string values with their inner quotes UNescaped:
  // `"caller_id": ""+15309335132" <+15309335132>"` is really
  // `"\"+15309335132\" <+15309335132>"`. Re-escape unescaped quotes inside a
  // line that is one bare string (starts and ends with `"`).
  t = t.split('\n').map((l) => {
    const m = l.match(/^(\s*)"(.*)"(\s*,?\s*)$/)
    return m && /(?<!\\)"/.test(m[2] ?? '')
      ? `${m[1]}"${(m[2] ?? '').replace(/(?<!\\)"/g, '\\"')}"${m[3]}`
      : l
  }).join('\n')
  // The viewer drops the commas between members — put them back wherever a
  // completed value is followed by a `"key":` line or a sibling container.
  t = t.replace(/([}\]"\w]|\d)\s*\n(\s*")/g, '$1,\n$2')
  t = t.replace(/([}\]])\s*\n(\s*[{[])/g, '$1,\n$2')
  // Stray commas the ellipsis strip may have left behind.
  t = t.replace(/{\s*,/g, '{').replace(/,\s*}/g, '}')
  t = t.replace(/\[\s*,/g, '[').replace(/,\s*\]/g, ']')
  // A paste that lost the outer braces.
  if (/^"/.test(t) || /^[\w-]+\s*:/.test(t)) t = `{${t}}`
  return t
}

function parseJsonList(text: string): Record<string, unknown>[] {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    parsed = JSON.parse(sanitizeViewerJson(text))
  }
  const list = Array.isArray(parsed) ? parsed : [parsed]
  return list as Record<string, unknown>[]
}

async function parseCdrFile(file: File): Promise<CdrRow[]> {
  const text = await file.text()
  const isJson = file.name.toLowerCase().endsWith('.json') || text.trim().startsWith('[') || text.trim().startsWith('{') || text.trim().startsWith('"')
  if (isJson) {
    // FreeSWITCH CDR_REPORT shapes map first; anything else falls back to
    // the generic flat-field aliases.
    return parseJsonList(text).map((r) => rowFromFreeSwitch(r) ?? rowFromRecord(r))
  }
  return parseCsv(text).map(rowFromRecord)
}

/** datetime-local expects "YYYY-MM-DDTHH:mm" — normalize loose CDR values. */
function toLocalInput(value: string): string | null {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

function baseName(name: string): string {
  return name.split(/[\\/]/).pop() ?? name
}

function sameFile(a: string, b: string): boolean {
  const na = baseName(a).toLowerCase()
  const nb = baseName(b).toLowerCase()
  if (na === nb || na.replace(/\.[^.]+$/, '') === nb.replace(/\.[^.]+$/, '')) return true
  // FreeSWITCH rows may carry just the call uuid — it usually appears inside
  // the recording's file name, so a containment match works there too.
  if (na.length >= 8 && nb.includes(na)) return true
  if (nb.length >= 8 && na.includes(nb)) return true
  return false
}

/* ------------------------------------------------------------------ */

export default function ManualCallUpload({ onDone }: { onDone: () => void }): JSX.Element | null {
  const toast = useToast()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [kind, setKind] = useState<UploadKind>('recording')
  const [items, setItems] = useState<UploadItem[]>([])
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [qmForms, setQmForms] = useState<QmFormOption[]>([])
  const [qmFormId, setQmFormId] = useState('')
  /** JSON typed/pasted by hand — same fields as the CDR file import. */
  const [cdrJson, setCdrJson] = useState('')
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const cdrRef = useRef<HTMLInputElement>(null)
  /** Hashes uploaded this page-session — survives drawer open/close. */
  const uploadedHashesRef = useRef<Set<string>>(new Set())
  const lastDupCheckRef = useRef('')

  useEffect(() => {
    let live = true
    api.get<{ enabled: boolean }>('/reports/calls/manual-upload/config')
      .then((res) => { if (live) setEnabled(Boolean(res.enabled)) })
      .catch(() => { if (live) setEnabled(false) })
    return () => { live = false }
  }, [])

  // The agent + QM-form dropdowns need tenant lists — fetched lazily the
  // first time the drawer opens (CALLS_VIEW-gated endpoints).
  useEffect(() => {
    if (!open || !enabled || agents.length > 0) return
    let live = true
    api.get<{ agents: AgentOption[] }>('/reports/calls/manual-upload/agents')
      .then((res) => { if (live) setAgents(res.agents ?? []) })
      .catch(() => { /* dropdown degrades to just the "none" option */ })
    api.get<{ forms: QmFormOption[] }>('/reports/calls/manual-upload/qm-forms')
      .then((res) => { if (live) setQmForms(res.forms ?? []) })
      .catch(() => { /* dropdown degrades to just the "default" option */ })
    return () => { live = false }
  }, [open, enabled])

  const reset = useCallback((): void => {
    setItems((prev) => {
      prev.forEach((i) => URL.revokeObjectURL(i.url))
      return []
    })
    setStep(1)
    setKind('recording')
    setUploading(false)
    setCdrJson('')
    setQmFormId('')
    lastDupCheckRef.current = ''
    if (inputRef.current) inputRef.current.value = ''
    if (cdrRef.current) cdrRef.current.value = ''
  }, [])

  const close = useCallback((): void => {
    if (uploading) return
    setOpen(false)
    reset()
  }, [uploading, reset])

  const addFiles = async (list: FileList | null): Promise<void> => {
    // Snapshot FIRST: FileList is live and clearing input.value empties it —
    // doing that inside/before the updater silently dropped every pick.
    const files = list ? Array.from(list) : []
    if (inputRef.current) inputRef.current.value = ''
    if (files.length === 0) return

    // In-list duplicate (same name + size) — warn instead of stacking it.
    const existing = new Set(items.map((i) => `${i.file.name}:${i.file.size}`))
    const fresh = files.filter((f) => !existing.has(`${f.name}:${f.size}`))
    const skipped = files.length - fresh.length
    if (skipped > 0) {
      toast.warning(
        'Duplicate skipped',
        `${skipped} file${skipped === 1 ? ' is' : 's are'} already in the list.`,
      )
    }
    if (fresh.length === 0) return

    const hashed = await Promise.all(
      fresh.map(async (file) => ({
        file,
        hash: await sha256Hex(file).catch(() => ''),
        textPreview:
          kind === 'chat' && !file.name.toLowerCase().endsWith('.pdf')
            ? (await file.text()).slice(0, 4000)
            : undefined,
      })),
    )
    setItems((prev) => [
      ...prev,
      ...hashed.map(({ file, hash, textPreview }) => ({
        file,
        url: URL.createObjectURL(file),
        durationSec: null,
        customerNumber: numberFromFilename(file.name),
        agentId: '',
        agentNumber: '',
        direction: 'inbound' as const,
        startTime: nowLocal(),
        status: file.size > MAX_FILE_BYTES ? ('error' as const) : ('idle' as const),
        error: file.size > MAX_FILE_BYTES ? 'File is larger than 190 MB.' : undefined,
        ...(hash ? { hash } : {}),
        ...(textPreview !== undefined ? { textPreview } : {}),
        duplicate: Boolean(hash && uploadedHashesRef.current.has(hash)),
      })),
    ])
  }

  const patchItem = (idx: number, patch: Partial<UploadItem>): void =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)))

  /** Apply parsed CDR rows onto the picked files — matched by fileName; a
   *  row with no fileName applies to every selected file. */
  const applyCdrRows = (rows: CdrRow[]): void => {
    if (rows.length === 0) {
      toast.error('No CDR rows found', 'Expected fields: fileName, customerNumber, agentNumber, direction, startTime, durationSec.')
      return
    }
    let matched = 0
    setItems((prev) => prev.map((it, idx) => {
      const row =
        rows.find((r) => r.fileName && sameFile(r.fileName, it.file.name))
        ?? rows.find((r) => !r.fileName)
        // A single CDR row + a single picked file is always the same call —
        // the recorded filename on the old platform rarely matches the name
        // the user uploads under.
        ?? (prev.length === 1 ? rows[0] : undefined)
        // Equal counts → pair them in order.
        ?? (rows.length === prev.length ? rows[idx] : undefined)
      if (!row) return it
      matched++
      const matchedAgent = row.agentNumber !== undefined
        ? agents.find((a) => a.extension !== null && a.extension === row.agentNumber)
        : undefined
      return {
        ...it,
        customerNumber: row.customerNumber ?? it.customerNumber,
        agentId: matchedAgent?.id ?? it.agentId,
        agentNumber: row.agentNumber ?? it.agentNumber,
        direction: row.direction ?? it.direction,
        startTime: (row.startTime ? toLocalInput(row.startTime) : null) ?? it.startTime,
        durationSec: row.durationSec ?? it.durationSec,
        queueName: row.queueName ?? it.queueName,
        hangupCause: row.hangupCause ?? it.hangupCause,
        linkedId: row.linkedId ?? it.linkedId,
        answerTime: row.answerTime ?? it.answerTime,
        billSec: row.billSec ?? it.billSec,
        queueWaitSec: row.queueWaitSec ?? it.queueWaitSec,
        hangupParty: row.hangupParty ?? it.hangupParty,
        did: row.did ?? it.did,
        destinationType: row.destinationType ?? it.destinationType,
        destination: row.destination ?? it.destination,
      }
    }))
    if (matched === 0) {
      toast.warning('No matches', `${rows.length} CDR row${rows.length === 1 ? '' : 's'} read but none matched a selected file name.`)
    } else {
      toast.success('CDR applied', `${matched} of ${items.length} file${items.length === 1 ? '' : 's'} matched.`)
    }
  }

  /** Optional CDR file: csv/json rows matched to picked files by name. */
  const applyCdrFile = async (list: FileList | null): Promise<void> => {
    const file = list?.[0]
    if (cdrRef.current) cdrRef.current.value = ''
    if (!file) return
    try {
      applyCdrRows(await parseCdrFile(file))
    } catch {
      toast.error('Could not read CDR file', 'Use .csv or .json with a fileName column.')
    }
  }

  /** Typed/pasted CDR JSON — one object or an array of the same fields. */
  const applyCdrJson = (): void => {
    const text = cdrJson.trim()
    if (text === '') return
    try {
      applyCdrRows(parseJsonList(text).map((r) => rowFromFreeSwitch(r) ?? rowFromRecord(r)))
    } catch {
      toast.error('Invalid JSON', 'Paste a JSON object like {"customerNumber":"+1555…","agentNumber":"1001","direction":"inbound","startTime":"2026-10-06T10:30","durationSec":95} or an array of them — a FreeSWITCH CDR_REPORT works too.')
    }
  }

  /**
   * The browser can't decode this codec (telephone WAVs are often μ-law/GSM).
   * Ask the API for a PCM transcode and swap the <audio> source — one try per
   * file so we don't loop on genuinely broken audio.
   */
  const loadConvertedPreview = async (idx: number): Promise<void> => {
    const it = items[idx]
    if (!it || it.previewTried) return
    patchItem(idx, { previewTried: true, previewBusy: true })
    try {
      const fd = new FormData()
      fd.append('file', it.file, it.file.name)
      const csrf = useAuthStore.getState().csrfToken
      const res = await fetch('/api/reports/calls/manual-upload/preview', {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'x-csrf-token': csrf } : {},
        body: fd,
      })
      if (!res.ok) throw new Error(`preview ${res.status}`)
      const blob = await res.blob()
      if (blob.size < 100) throw new Error('empty preview')
      const url = URL.createObjectURL(blob)
      patchItem(idx, { url, previewConverted: true, previewBusy: false })
    } catch {
      patchItem(idx, { previewBusy: false, previewFailed: true })
    }
  }

  const removeItem = (idx: number): void =>
    setItems((prev) => {
      const gone = prev[idx]
      if (gone) URL.revokeObjectURL(gone.url)
      return prev.filter((_, i) => i !== idx)
    })

  // Server-side duplicate check whenever the item signature (hashes +
  // durations) changes — duration lands late via <audio> metadata. Runs in
  // step 2 as well so a re-upload is flagged BEFORE review.
  useEffect(() => {
    if (!open || items.length === 0) return
    const signature = items.map((i) => `${i.hash ?? ''}:${i.durationSec ?? ''}`).join('|')
    if (signature === lastDupCheckRef.current) return
    lastDupCheckRef.current = signature
    let live = true
    api.post<{ files: { duplicate: boolean; possible: boolean }[] }>(
      '/reports/calls/manual-upload/check',
      {
        files: items.map((i) => ({
          ...(i.hash ? { hash: i.hash } : {}),
          size: i.file.size,
          ...(i.durationSec !== null && Number.isFinite(i.durationSec)
            ? { durationSec: i.durationSec }
            : {}),
        })),
      },
    ).then((res) => {
      if (!live) return
      setItems((prev) =>
        prev.map((it, idx) => {
          const r = res.files[idx]
          if (!r) return it
          return {
            ...it,
            duplicate: it.duplicate || r.duplicate,
            possibleDup: it.possibleDup || r.possible,
          }
        }),
      )
    }).catch(() => { /* warning is best-effort */ })
    return () => { live = false }
  }, [open, step, items])

  const canUpload =
    items.length > 0 &&
    items.some((i) => i.status !== 'done') &&
    !uploading

  const upload = async (): Promise<void> => {
    // Validate FIRST and mark the offending cards — a silently disabled
    // button reads as "nothing happens".
    const invalid = items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it.status !== 'done' && it.customerNumber.trim() === '')
    if (invalid.length === items.filter((i) => i.status !== 'done').length && invalid.length > 0) {
      invalid.forEach(({ idx }) =>
        patchItem(idx, { status: 'error', error: 'Customer number is required.' }))
      toast.error('Customer number required', 'Fill in the customer number on each file before uploading.')
      return
    }
    invalid.forEach(({ idx }) =>
      patchItem(idx, { status: 'error', error: 'Customer number is required — this file was skipped.' }))

    setUploading(true)
    // Track results locally — `items` here is a snapshot; per-file statuses
    // change through setState during the loop.
    let failed = 0
    for (let idx = 0; idx < items.length; idx++) {
      const it = items[idx]
      if (!it || it.status === 'done' || it.customerNumber.trim() === '' || (it.file.size > MAX_FILE_BYTES)) continue
      patchItem(idx, { status: 'uploading', error: undefined })
      const fd = new FormData()
      // `meta` BEFORE the file so the API sees the fields first.
      fd.append('meta', JSON.stringify({
        kind,
        customerNumber: it.customerNumber.trim(),
        ...(it.agentId !== '' ? { agentId: it.agentId } : {}),
        ...(it.agentNumber.trim() ? { agentNumber: it.agentNumber.trim() } : {}),
        ...(qmFormId !== '' ? { qmFormId } : {}),
        direction: it.direction,
        startTime: it.startTime ? new Date(it.startTime).toISOString() : undefined,
        ...(it.queueName !== undefined && it.queueName !== '' ? { queueName: it.queueName } : {}),
        ...(it.hangupCause !== undefined && it.hangupCause !== '' ? { hangupCause: it.hangupCause } : {}),
        ...(it.linkedId !== undefined && it.linkedId !== '' ? { linkedId: it.linkedId } : {}),
        ...(it.answerTime !== undefined ? { answerTime: it.answerTime } : {}),
        ...(it.billSec !== undefined ? { billSec: it.billSec } : {}),
        ...(it.queueWaitSec !== undefined ? { queueWaitSec: it.queueWaitSec } : {}),
        ...(it.hangupParty !== undefined ? { hangupParty: it.hangupParty } : {}),
        ...(it.did !== undefined && it.did !== '' ? { did: it.did } : {}),
        ...(it.destinationType !== undefined && it.destinationType !== '' ? { destinationType: it.destinationType } : {}),
        ...(it.destination !== undefined && it.destination !== '' ? { destination: it.destination } : {}),
        ...(kind === 'recording' && it.durationSec !== null && Number.isFinite(it.durationSec)
          ? { durationSec: Math.round(it.durationSec) }
          : {}),
      }))
      fd.append('file', it.file, it.file.name)
      try {
        await api.post('/reports/calls/manual-upload', fd)
        if (it.hash) uploadedHashesRef.current.add(it.hash)
        patchItem(idx, { status: 'done' })
      } catch (err) {
        failed++
        patchItem(idx, { status: 'error', error: (err as Error).message })
      }
    }
    setUploading(false)

    if (failed === 0) {
      toast.success(
        items.length === 1 ? (kind === 'chat' ? 'Transcript uploaded' : 'Call uploaded')
          : `${items.length} ${kind === 'chat' ? 'transcripts' : 'calls'} uploaded`,
        kind === 'chat'
          ? 'Quality scoring will run automatically — the record appears in this report.'
          : 'Transcription and quality scoring will run automatically — the calls appear in this report.',
      )
      onDone()
      setOpen(false)
      reset()
    } else {
      toast.error(`${failed} file${failed === 1 ? '' : 's'} failed to upload`, 'Fix the error below and press Upload again — finished files are kept.')
    }
  }

  if (!enabled) return null

  const FileIcon = kind === 'chat' ? FileText : FileAudio
  const stepTitle =
    step === 1 ? 'Upload — what are you adding?' :
    step === 2 ? (kind === 'chat' ? 'Upload — choose transcripts & details' : 'Upload — choose recordings & details')
    : 'Upload — review & confirm'

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`}
        title="Upload external calls or chat transcripts"
      >
        <Upload className="h-4 w-4 text-slate-400" />
        Upload
      </button>

      <Drawer isOpen={open} onClose={close} title={stepTitle}>
        <div className="flex flex-col gap-4">
          {/* Step indicator */}
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <span className={step === 1 ? 'text-[#3366FF]' : ''}>1 · Type</span>
            <span className="h-px w-5 bg-slate-200" />
            <span className={step === 2 ? 'text-[#3366FF]' : ''}>2 · Details</span>
            <span className="h-px w-5 bg-slate-200" />
            <span className={step === 3 ? 'text-[#3366FF]' : ''}>3 · Review & upload</span>
          </div>

          {step === 1 && (
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => { setKind('recording'); setStep(2) }}
                className={`flex items-center gap-4 ${R} border border-slate-200 bg-white p-4 text-left transition-colors hover:border-[#3366FF]/60 hover:bg-blue-50/40`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF]/10">
                  <Phone className="h-5 w-5 text-[#3366FF]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-slate-900">Call recording</p>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    Audio file(s) from an external system. Encrypted on upload, then transcribed and QM-scored like live calls.
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setKind('chat'); setStep(2) }}
                className={`flex items-center gap-4 ${R} border border-slate-200 bg-white p-4 text-left transition-colors hover:border-[#3366FF]/60 hover:bg-blue-50/40`}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF]/10">
                  <MessageSquare className="h-5 w-5 text-[#3366FF]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-slate-900">Chat transcript</p>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    A saved chat conversation (.txt, .json, .csv, .pdf). The text becomes the transcript — the AI quality agent scores it directly.
                  </p>
                </div>
              </button>
            </div>
          )}

          {step === 2 && (
            <>
              <button
                onClick={() => inputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-2 ${R} border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-slate-500 transition-colors hover:border-[#3366FF]/40 hover:bg-blue-50/50 hover:text-[#3366FF]`}
              >
                <FileIcon className="h-8 w-8" />
                <span className="text-[13px] font-semibold">
                  {kind === 'chat' ? 'Choose transcript files' : 'Choose audio files'}
                </span>
                <span className="text-[11px] text-slate-400">
                  {kind === 'chat'
                    ? 'One file per conversation · txt, json, csv, md, pdf'
                    : 'One file per call · wav, mp3, m4a, ogg, flac, webm, amr'}
                </span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept={kind === 'chat' ? ACCEPT_TEXT : ACCEPT_AUDIO}
                multiple
                className="hidden"
                onChange={(e) => { void addFiles(e.target.files) }}
              />

              {kind === 'recording' && (
                <button
                  onClick={() => cdrRef.current?.click()}
                  className={`flex w-full items-center gap-3 ${R} border border-slate-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-[#3366FF]/50`}
                >
                  <FileUp className="h-4 w-4 shrink-0 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold text-slate-700">Import CDR file <span className="font-normal text-slate-400">(optional)</span></p>
                    <p className="text-[11px] text-slate-400">
                      .csv or .json with fileName, customerNumber, agentNumber, direction, startTime, durationSec — or edit the fields below.
                    </p>
                  </div>
                </button>
              )}
              <input
                ref={cdrRef}
                type="file"
                accept=".csv,.json"
                className="hidden"
                onChange={(e) => { void applyCdrFile(e.target.files) }}
              />

              {kind === 'recording' && (
                <div className={`${R} border border-slate-200 bg-white p-3`}>
                  <p className="text-[12.5px] font-semibold text-slate-700">
                    Or type the CDR as JSON <span className="font-normal text-slate-400">(optional)</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    One object, or an array with a <code className="font-mono">fileName</code> per call — same fields as the CDR file.
                  </p>
                  <textarea
                    value={cdrJson}
                    onChange={(e) => setCdrJson(e.target.value)}
                    rows={4}
                    placeholder='{"fileName":"call.wav","customerNumber":"+15551234567","agentNumber":"1001","direction":"inbound","startTime":"2026-10-06T10:30","durationSec":95}'
                    className={`mt-2 block w-full ${R} border border-slate-200 px-2 py-1.5 font-mono text-[11.5px] text-slate-800 outline-none focus:border-[#3366FF]`}
                  />
                  <button
                    onClick={applyCdrJson}
                    disabled={cdrJson.trim() === '' || items.length === 0}
                    className={`mt-2 h-7 px-3 ${R} border border-slate-200 text-[11.5px] font-semibold text-slate-600 transition-colors hover:border-[#3366FF]/50 hover:text-[#3366FF] disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    Apply CDR to {items.length > 0 ? `${items.length} file${items.length === 1 ? '' : 's'}` : 'files'}
                  </button>
                </div>
              )}

              {items.length > 0 && (
                <ul className="flex flex-col gap-3">
                  {items.map((it, idx) => (
                    <li key={idx} className={`${R} border border-slate-100 bg-white p-3`}>
                      <div className="flex items-center gap-3">
                        <FileIcon className="h-4 w-4 shrink-0 text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[12.5px] font-medium text-slate-800">{it.file.name}</p>
                          <p className="text-[11px] text-slate-400">{fmtBytes(it.file.size)}</p>
                        </div>
                        <button
                          onClick={() => removeItem(idx)}
                          className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                          aria-label={`Remove ${it.file.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {it.duplicate && (
                        <p className="mt-1.5 flex items-center gap-1 text-[10.5px] font-medium text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          already uploaded — uploading again creates a duplicate
                        </p>
                      )}
                      {!it.duplicate && it.possibleDup && (
                        <p className="mt-1.5 flex items-center gap-1 text-[10.5px] text-amber-600">
                          <AlertTriangle className="h-3 w-3" />
                          a matching record exists — possible duplicate
                        </p>
                      )}

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <label className="flex flex-col gap-1">
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                            Customer number *
                          </span>
                          <input
                            value={it.customerNumber}
                            onChange={(e) => patchItem(idx, { customerNumber: e.target.value })}
                            placeholder="+1 555 123 4567"
                            required
                            className={`h-8 ${R} border px-2 text-[12.5px] text-slate-800 outline-none focus:border-[#3366FF] ${
                              it.customerNumber.trim() === '' ? 'border-red-300 bg-red-50/40' : 'border-slate-200'
                            }`}
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                            Agent
                          </span>
                          <Select
                            ariaLabel="Agent"
                            size="sm"
                            value={it.agentId}
                            onChange={(v) => patchItem(idx, { agentId: v })}
                          >
                            <option value="">Unspecified</option>
                            {agents.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name ?? 'Agent'}{a.extension !== null && a.extension !== '' ? ` · ${a.extension}` : ''}
                              </option>
                            ))}
                          </Select>
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                            {kind === 'chat' ? 'Initiated by' : 'Direction'}
                          </span>
                          <Select
                            ariaLabel={kind === 'chat' ? 'Initiated by' : 'Direction'}
                            size="sm"
                            value={it.direction}
                            onChange={(v) => patchItem(idx, { direction: v as 'inbound' | 'outbound' })}
                          >
                            <option value="inbound">{kind === 'chat' ? 'Customer' : 'Inbound'}</option>
                            <option value="outbound">{kind === 'chat' ? 'Agent' : 'Outbound'}</option>
                          </Select>
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                            {kind === 'chat' ? 'Conversation time' : 'Call time'}
                          </span>
                          <input
                            type="datetime-local"
                            value={it.startTime}
                            onChange={(e) => patchItem(idx, { startTime: e.target.value })}
                            className={`h-8 ${R} border border-slate-200 px-2 text-[12.5px] text-slate-800 outline-none focus:border-[#3366FF]`}
                          />
                        </label>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {qmForms.length > 0 && (
                <label className={`flex items-center justify-between gap-3 ${R} border border-slate-200 bg-white px-3 py-2`}>
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-semibold text-slate-700">QM form</span>
                    <span className="block text-[11px] text-slate-400">
                      The AI scores the uploaded {kind === 'chat' ? 'transcripts' : 'calls'} against the selected form.
                    </span>
                  </span>
                  <Select
                    ariaLabel="QM form"
                    size="sm"
                    className="w-56 shrink-0"
                    value={qmFormId}
                    onChange={setQmFormId}
                  >
                    <option value="">Default form</option>
                    {qmForms.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}{f.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </Select>
                </label>
              )}

              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setStep(1)}
                  className={`h-8 px-3 ${R} border border-slate-200 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300`}
                >
                  ← Type
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={items.length === 0}
                  className={`h-8 px-4 ${R} bg-[#3366FF] text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  Review {items.length > 0 ? `${items.length} file${items.length === 1 ? '' : 's'}` : ''} →
                </button>
              </div>
              {items.some((i) => i.customerNumber.trim() === '') && (
                <p className="text-right text-[11px] text-amber-600">
                  A customer number is required for every file — it is what client lookup matches on.
                </p>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <ul className="flex flex-col gap-3">
                {items.map((it, idx) => (
                  <li
                    key={idx}
                    className={`${R} border bg-white p-3 ${
                      it.status === 'error' ? 'border-red-200' :
                      it.status === 'done' ? 'border-emerald-200' : 'border-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileIcon className="h-4 w-4 shrink-0 text-slate-400" />
                      <p className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-slate-800" title={it.file.name}>
                        {it.file.name}
                      </p>
                      <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                        {fmtBytes(it.file.size)}{kind === 'recording' ? ` · ${fmtSec(it.durationSec)}` : ''}
                      </span>
                      {it.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin text-[#3366FF]" />}
                      {it.status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      {it.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                    </div>

                    {kind === 'recording' && (
                      <>
                        <audio
                          controls
                          preload="metadata"
                          src={it.url}
                          className="mt-2 h-8 w-full"
                          onError={() => { void loadConvertedPreview(idx) }}
                          onLoadedMetadata={(e) => {
                            const d = (e.target as HTMLAudioElement).duration
                            if (Number.isFinite(d)) patchItem(idx, { durationSec: d })
                          }}
                        />
                        {it.previewBusy && (
                          <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                            <Loader2 className="h-3 w-3 animate-spin" /> Converting for preview…
                          </p>
                        )}
                        {it.previewConverted && (
                          <p className="mt-1 text-[11px] text-slate-400">
                            Converted preview — the original codec is normalized to WAV on upload.
                          </p>
                        )}
                        {it.previewFailed && (
                          <p className="mt-1 text-[11px] text-amber-600">
                            Browser can’t preview this file — it will still be converted on upload.
                          </p>
                        )}
                      </>
                    )}

                    {kind === 'chat' && (
                      it.textPreview !== undefined ? (
                        <pre className="mt-2 max-h-36 overflow-auto whitespace-pre-wrap rounded-[3px] border border-slate-100 bg-slate-50 p-2 font-mono text-[11px] leading-relaxed text-slate-600">
                          {it.textPreview === '' ? '(empty file)' : it.textPreview}
                        </pre>
                      ) : (
                        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
                          <FileText className="h-3.5 w-3.5" />
                          PDF transcript — text is extracted on upload.
                        </p>
                      )
                    )}

                    <p className="mt-2 text-[11.5px] text-slate-500">
                      {it.customerNumber.trim() !== '' ? it.customerNumber : '—'}
                      {' · '}
                      {(() => {
                        const a = agents.find((x) => x.id === it.agentId)
                        return a !== undefined
                          ? `Agent ${a.name ?? ''}${a.extension !== null && a.extension !== '' ? ` (${a.extension})` : ''}`.trim()
                          : it.agentNumber.trim() !== '' ? `Agent ${it.agentNumber}` : 'No agent'
                      })()}
                      {' · '}{it.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                      {it.startTime !== '' ? ` · ${it.startTime.replace('T', ' ')}` : ''}
                      {it.customerNumber.trim() === '' && (
                        <span className="ml-1 font-semibold text-red-600">— customer number required, go back to Details</span>
                      )}
                    </p>

                    {it.duplicate && (
                      <p className="mt-2 flex items-center gap-1.5 rounded-[3px] bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        This exact file was already uploaded — uploading again creates a duplicate.
                      </p>
                    )}
                    {!it.duplicate && it.possibleDup && (
                      <p className="mt-2 flex items-center gap-1.5 rounded-[3px] bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        A matching record already exists — possible duplicate.
                      </p>
                    )}

                    {it.status === 'error' && it.error && (
                      <p className="mt-2 text-[11.5px] text-red-600">{it.error}</p>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setStep(2)}
                  disabled={uploading}
                  className={`h-8 px-3 ${R} border border-slate-200 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 disabled:opacity-40`}
                >
                  ← Details
                </button>
                <button
                  onClick={() => void upload()}
                  disabled={!canUpload}
                  className={`flex h-8 items-center gap-2 px-4 ${R} bg-[#3366FF] text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {uploading ? 'Uploading…' : `Upload ${items.length} ${kind === 'chat' ? 'transcript' : 'call'}${items.length === 1 ? '' : 's'}`}
                </button>
              </div>
              {items.some((i) => i.customerNumber.trim() === '') && (
                <p className="text-right text-[11px] text-amber-600">
                  A customer number is required for every file — it is what client lookup matches on.
                </p>
              )}
            </>
          )}
        </div>
      </Drawer>
    </>
  )
}
